import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft, MessageCircle, FileText, Shield, Wrench, Package,
  Phone, Download, Camera, Wifi, Moon, ShoppingCart
} from "lucide-react";
import { useCatalogStore } from "../admin/store/catalogStore";
import RequestQuoteModal from "../components/RequestQuoteModal";
import BuyNowModal from "../components/BuyNowModal";

function formatLKR(v: number) {
  return `Rs. ${v.toLocaleString("en-LK")}`;
}

function SpecRow({ label, value }: { label: string; value?: string | boolean | number }) {
  if (value === undefined || value === null || value === "" || value === false) return null;
  const displayValue = value === true ? "Yes" : String(value);
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-[13px] text-gray-500">{label}</span>
      <span className="text-[13px] font-medium text-gray-900 text-right max-w-[60%]">{displayValue}</span>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { brands, categories, catalogProducts } = useCatalogStore();
  const [activeImg, setActiveImg] = useState("");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);

  const product = catalogProducts.find((p) => p.id === id);
  const brand = brands.find((b) => b.id === product?.brandId);
  const category = categories.find((c) => c.id === product?.categoryId);
  const subcategory = categories.find((c) => c.id === product?.subcategoryId);

  const allImages = [
    ...(product?.mainImage ? [{ id: "main", url: product.mainImage }] : []),
    ...(product?.galleryImages ?? []),
  ];

  useEffect(() => {
    if (allImages[0]?.url) setActiveImg(allImages[0].url);
  }, [id, product?.mainImage]);

  const related = catalogProducts
    .filter((p) => p.id !== id && p.status === "active" && (p.brandId === product?.brandId || p.categoryId === product?.categoryId))
    .slice(0, 4);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-200" />
          <h2 className="text-[22px] font-semibold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-500 mb-6">This product may have been removed or the link is invalid.</p>
          <Link to="/products" className="h-11 px-6 rounded-2xl bg-gray-900 text-white text-[14px] font-medium inline-flex items-center gap-2">
            <ChevronLeft className="w-4 h-4" /> Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const whatsappNumber = "94702161323";
  const whatsappMsg = `Hi K-Tec! I'm interested in the ${product.name} (${product.modelNumber ?? product.sku}). Price: ${formatLKR(product.regularPrice)}. Please send me more information.`;
  const isCam = ["Bullet Camera", "Dome Camera", "Turret Camera", "PTZ Camera", "Box Camera"].includes(product.productType ?? "");

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 text-[13px] text-gray-500 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-gray-900">Home</Link>
          <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
          <Link to="/products" className="hover:text-gray-900">Products</Link>
          {category && (
            <>
              <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
              <span>{category.name}</span>
            </>
          )}
          {subcategory && (
            <>
              <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
              <span>{subcategory.name}</span>
            </>
          )}
          <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
          <span className="text-gray-900 font-medium truncate max-w-[200px]">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* ── Main Product Section ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
          {/* Images */}
          <div className="space-y-3">
            <div className="bg-white rounded-3xl border border-gray-200/60 aspect-square flex items-center justify-center overflow-hidden p-6">
              {activeImg ? (
                <img src={activeImg} alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <Package className="w-20 h-20 text-gray-200" />
              )}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {allImages.map((img) => (
                  <button
                    key={img.id}
                    onClick={() => setActiveImg(img.url)}
                    className={`shrink-0 w-16 h-16 rounded-xl border-2 overflow-hidden bg-white transition-all ${activeImg === img.url ? "border-[#7FA86A]" : "border-gray-200/60 hover:border-gray-300"}`}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              {brand && <span className="text-[13px] font-bold text-[#7FA86A] uppercase tracking-wider">{brand.name}</span>}
              {product.isBestSeller && <span className="bg-[#7FA86A] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">Best Seller</span>}
              {product.isNewProduct && <span className="bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">New</span>}
              {product.isFeatured && <span className="bg-violet-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">Featured</span>}
            </div>

            <h1 className="text-[28px] md:text-[34px] font-semibold text-gray-900 leading-tight tracking-tight">{product.name}</h1>

            {product.modelNumber && (
              <p className="text-[14px] text-gray-500">Model: <span className="font-mono font-medium text-gray-700">{product.modelNumber}</span></p>
            )}
            {product.shortDescription && (
              <p className="text-[15px] text-gray-600 leading-relaxed">{product.shortDescription}</p>
            )}

            {/* Price */}
            <div className="flex items-baseline gap-3">
              {product.salePrice && product.salePrice < product.regularPrice ? (
                <>
                  <span className="text-[34px] font-bold text-[#7FA86A]">{formatLKR(product.salePrice)}</span>
                  <span className="text-[18px] text-gray-400 line-through">{formatLKR(product.regularPrice)}</span>
                  <span className="bg-red-100 text-red-600 text-[11px] font-bold px-2 py-0.5 rounded-full">SALE</span>
                </>
              ) : (
                <span className="text-[34px] font-bold text-gray-900">{formatLKR(product.regularPrice)}</span>
              )}
            </div>
            {product.installationPrice && product.installationPrice > 0 && (
              <p className="text-[13px] text-gray-400 -mt-2">+ {formatLKR(product.installationPrice)} installation service</p>
            )}

            {/* Stock */}
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${product.inStock ? "bg-emerald-400" : "bg-red-400"}`} />
              <span className={`text-[14px] font-medium ${product.inStock ? "text-emerald-600" : "text-red-500"}`}>
                {product.inStock ? "In Stock" : "Out of Stock"}
              </span>
              {product.inStock && product.stock > 0 && <span className="text-[12px] text-gray-400">({product.stock} units)</span>}
            </div>

            {/* Key Specs for cameras */}
            {isCam && (
              <div className="grid grid-cols-2 gap-2">
                {product.resolution && (
                  <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-[#7FA86A]" />
                    <div>
                      <p className="text-[10px] text-gray-400">Resolution</p>
                      <p className="text-[12px] font-semibold text-gray-800">{product.resolution}</p>
                    </div>
                  </div>
                )}
                {product.nightVisionDistance && (
                  <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-2">
                    <Moon className="w-4 h-4 text-[#7FA86A]" />
                    <div>
                      <p className="text-[10px] text-gray-400">Night Vision</p>
                      <p className="text-[12px] font-semibold text-gray-800">{product.nightVisionDistance}</p>
                    </div>
                  </div>
                )}
                {product.hasWiFi !== undefined && (
                  <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-[#7FA86A]" />
                    <div>
                      <p className="text-[10px] text-gray-400">Wi-Fi</p>
                      <p className="text-[12px] font-semibold text-gray-800">{product.hasWiFi ? "Supported" : "No Wi-Fi"}</p>
                    </div>
                  </div>
                )}
                {product.ipRating && (
                  <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#7FA86A]" />
                    <div>
                      <p className="text-[10px] text-gray-400">IP Rating</p>
                      <p className="text-[12px] font-semibold text-gray-800">{product.ipRating}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setShowBuyNowModal(true)}
                disabled={!product.inStock}
                className="w-full py-3.5 px-6 rounded-2xl bg-[#7FA86A] hover:bg-[#658C53] disabled:opacity-50 text-white font-bold text-[16px] flex items-center justify-center gap-2 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" /> Buy Now
              </button>
              <div className="flex gap-3">
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMsg)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 px-6 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-[14px] flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <button
                  onClick={() => setShowQuoteModal(true)}
                  className="flex-1 py-3 px-6 rounded-2xl bg-[#1D1D1F] hover:bg-gray-800 text-white font-semibold text-[14px] flex items-center justify-center gap-2 transition-colors"
                >
                  <FileText className="w-4 h-4" /> Request Quote
                </button>
              </div>
            </div>

            {/* Warranty + Datasheet */}
            <div className="flex flex-wrap gap-4">
              {product.warrantyPeriod && (
                <div className="flex items-center gap-2 text-[13px] text-gray-600">
                  <Shield className="w-4 h-4 text-[#7FA86A]" />
                  {product.warrantyPeriod} Warranty
                </div>
              )}
              {product.datasheetUrl && (
                <a href={product.datasheetUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[13px] text-[#7FA86A] hover:underline">
                  <Download className="w-4 h-4" /> Download Datasheet
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── Specs / Description ────────────────────────────────────────────── */}
        <div className="bg-white rounded-3xl border border-gray-200/60 overflow-hidden mb-12">
          {product.fullDescription && (
            <div className="p-6 md:p-8 border-b border-gray-100">
              <h2 className="text-[18px] font-semibold text-gray-900 mb-3">Description</h2>
              <p className="text-[15px] text-gray-600 leading-relaxed whitespace-pre-line">{product.fullDescription}</p>
            </div>
          )}

          {isCam && (
            <div className="p-6 md:p-8 border-b border-gray-100">
              <h2 className="text-[18px] font-semibold text-gray-900 mb-4">Technical Specifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <div>
                  <SpecRow label="Resolution" value={product.resolution} />
                  <SpecRow label="Megapixels" value={product.megapixels ? `${product.megapixels}MP` : undefined} />
                  <SpecRow label="Lens Size" value={product.lensSize} />
                  <SpecRow label="Night Vision Distance" value={product.nightVisionDistance} />
                  <SpecRow label="Lighting Type" value={product.lightingType} />
                  <SpecRow label="Usage" value={product.usage} />
                  <SpecRow label="IP Rating" value={product.ipRating} />
                  <SpecRow label="Power Requirements" value={product.powerRequirements} />
                </div>
                <div>
                  <SpecRow label="Built-in Microphone" value={product.hasAudio} />
                  <SpecRow label="Two-Way Audio" value={product.hasTwoWayAudio} />
                  <SpecRow label="AI Human Detection" value={product.hasAIHumanDetection} />
                  <SpecRow label="Vehicle Detection" value={product.hasVehicleDetection} />
                  <SpecRow label="Motion Detection" value={product.hasMotionDetection} />
                  <SpecRow label="Smart Tracking" value={product.hasSmartTracking} />
                  <SpecRow label="Wi-Fi Support" value={product.hasWiFi} />
                  <SpecRow label="PoE Support" value={product.hasPOE} />
                  <SpecRow label="Storage Support" value={product.storageSupport} />
                  <SpecRow label="Mobile App" value={product.mobileAppSupport} />
                  <SpecRow label="Compatible Recorder" value={product.compatibleRecorder} />
                </div>
              </div>
            </div>
          )}

          {product.specs && product.specs.length > 0 && (
            <div className="p-6 md:p-8 border-b border-gray-100">
              <h2 className="text-[18px] font-semibold text-gray-900 mb-4">Additional Specifications</h2>
              {product.specs.map((spec) => (
                <SpecRow key={spec.key} label={spec.label} value={`${spec.value}${spec.unit ? " " + spec.unit : ""}`} />
              ))}
            </div>
          )}

          {product.installationNotes && (
            <div className="p-6 md:p-8">
              <h2 className="text-[18px] font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#7FA86A]" /> Installation Notes
              </h2>
              <p className="text-[14px] text-gray-600 leading-relaxed whitespace-pre-line">{product.installationNotes}</p>
            </div>
          )}
        </div>

        {/* ── Related Products ──────────────────────────────────────────────── */}
        {related.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[22px] font-semibold text-gray-900">Related Products</h2>
              <Link to="/products" className="text-[13px] text-[#7FA86A] hover:underline flex items-center gap-1">
                View All <ChevronLeft className="w-4 h-4 rotate-180" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {related.map((p) => {
                const b = brands.find((br) => br.id === p.brandId);
                return (
                  <Link key={p.id} to={`/products/${p.id}`} className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden hover:shadow-md transition-shadow group">
                    <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                      {p.mainImage ? (
                        <img src={p.mainImage} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform" />
                      ) : <Package className="w-8 h-8 text-gray-200" />}
                    </div>
                    <div className="p-3">
                      {b && <p className="text-[10px] text-[#7FA86A] font-bold uppercase">{b.name}</p>}
                      <p className="text-[13px] font-medium text-gray-900 truncate">{p.name}</p>
                      <p className="text-[13px] font-semibold text-gray-900 mt-1">{formatLKR(p.regularPrice)}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-[#1D1D1F] rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-[22px] font-semibold text-white mb-1">Need Help with This Product?</h2>
            <p className="text-gray-400 text-[14px]">Our specialists are ready to assist with installation and setup.</p>
          </div>
          <div className="flex gap-3">
            <a
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMsg)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="h-11 px-5 rounded-2xl bg-emerald-500 text-white font-semibold text-[14px] hover:bg-emerald-600 flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href="tel:0762829800" className="h-11 px-5 rounded-2xl bg-white/10 text-white font-semibold text-[14px] hover:bg-white/20 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Call Us
            </a>
          </div>
        </div>
      </div>

      {/* Request Quote Modal */}
      {product && (
        <RequestQuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          item={{
            id: product.id,
            name: product.name,
            type: 'product',
            price: product.salePrice && product.salePrice < product.regularPrice ? product.salePrice : product.regularPrice,
            image: product.mainImage,
            source: 'product_detail',
          }}
        />
      )}

      {/* Buy Now Modal */}
      {product && (
        <BuyNowModal
          isOpen={showBuyNowModal}
          onClose={() => setShowBuyNowModal(false)}
          item={{
            id: product.id,
            name: product.name,
            type: 'product',
            price: product.salePrice && product.salePrice < product.regularPrice ? product.salePrice : product.regularPrice,
            image: product.mainImage,
            source: 'product_detail',
          }}
        />
      )}
    </div>
  );
}
